import React, { useState, useRef } from "react"
import { useSelector } from "react-redux"
import { convertMinutesTo12HHMM } from "../../utils/generalUtils"
import useFetchSettings from "../../hooks/useFetchSettings"

const NurseLunchBreakComponent = () => {
	const nurseSchedule = useSelector(state => state.schedule.nurseSchedule)
	const { settingsData } = useFetchSettings()
	const breakDuration = settingsData?.break_duration

	const [showFullReport, setShowFullReport] = useState(true) // Start expanded by default
	const bottomRef = useRef(null) // Add ref for scrolling

	const getLunchStatusBadge = (actualStart, actualEnd, scheduledStartTime) => {
		const badges = []

		// First badge - Lunch status
		if (!actualStart) {
			badges.push(
				<span
					key="status"
					className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800 mr-2"
				>
					Not Started
				</span>
			)
		} else if (actualStart && !actualEnd) {
			badges.push(
				<span
					key="status"
					className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-200 text-blue-800 mr-2"
				>
					On Lunch
				</span>
			)
		} else if (actualStart && actualEnd) {
			const duration = actualEnd - actualStart
			if (duration >= breakDuration) {
				badges.push(
					<span
						key="status"
						className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-200 text-green-800 mr-2"
					>
						Completed
					</span>
				)
			} else {
				badges.push(
					<span
						key="status"
						className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-200 text-yellow-800 mr-2"
					>
						Incomplete
					</span>
				)
			}
		}

		// Second badge - Late lunch check (4+ hours after shift start)
		// Only show "Late Lunch" when actualStart exists and is more than 4 hours after shift start
		if (actualStart && actualStart > scheduledStartTime + 240) {
			badges.push(
				<span
					key="late"
					className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-200 text-red-800"
				>
					Late Lunch
				</span>
			)
		}

		return <div className="flex items-center">{badges}</div>
	}

	const handleToggleReport = () => {
		setShowFullReport(!showFullReport)
		if (!showFullReport) {
			setTimeout(() => {
				bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
			}, 0)
		}
	}

	return (
		<>
			<div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
				<div className="flex justify-between items-center mb-6">
					<h3 className="text-xl font-medium text-gray-900">
						Nurse Lunch Break Compliance
					</h3>
				</div>

				{/* Nurse Information */}
				<div>
					{/* Column Headers - Updated styling */}
					<div className="grid grid-cols-12 gap-4 mb-2 mt-4 text-bold text-sm font-medium uppercase text-left">
						<div className="col-span-5 p-2">Nurse</div>
						<div className="col-span-4 p-2">Actual Lunch Break</div>
						<div className="col-span-3 p-2 text-left">Scheduled Lunch Break</div>
					</div>

					{/* List of nurses */}
					<div className="mt-2">
						{nurseSchedule.length > 0 ? (
							(showFullReport ? nurseSchedule : nurseSchedule.slice(0, 5)).map(
								(nurse, index) => (
									<div
										key={nurse.nurseId}
										className="grid grid-cols-12 gap-4 items-center mb-4 text-left relative"
									>
										<div className="col-span-5 text-bold text-md font-medium text-gray-900 justify-start p-2">
											{nurse.nurseName}
										</div>
										<div className="col-span-2 text-md text-gray-900 flex items-center justify-start p-2">
											<span className="mr-2">
												{nurse.actualLunchBreakStart
													? nurse.actualLunchBreakEnd
														? `${convertMinutesTo12HHMM(nurse.actualLunchBreakStart)} - ${convertMinutesTo12HHMM(nurse.actualLunchBreakEnd)}`
														: `${convertMinutesTo12HHMM(nurse.actualLunchBreakStart)} - Ongoing`
													: "Not Started"}
											</span>
										</div>

										<div className="col-span-2 text-md text-gray-900 text-left flex justify-start p-2">
											{getLunchStatusBadge(
												nurse.actualLunchBreakStart,
												nurse.actualLunchBreakEnd,
												nurse.scheduledStartTime
											)}
										</div>

										<div className="col-span-3 text-md text-gray-900 text-left p-2">
											{nurse.scheduledLunchBreakStart != null &&
											nurse.scheduledLunchBreakEnd != null
												? `${convertMinutesTo12HHMM(nurse.scheduledLunchBreakStart)} - ${convertMinutesTo12HHMM(nurse.scheduledLunchBreakEnd)}`
												: "Not Set"}
										</div>
									</div>
								)
							)
						) : (
							<div className="text-sm text-gray-500 text-center">
								No nurse data available.
							</div>
						)}
					</div>

					{/* Divider Line */}
					<hr className="border-t border-gray-300 my-4" />

					{nurseSchedule.length > 5 && (
						<div className="text-center mt-2">
							<button
								className="text-customBlue text-sm font-medium"
								onClick={handleToggleReport}
							>
								{showFullReport
									? "Collapse Report"
									: `View full report (${nurseSchedule.length - 5} more)`}
							</button>
						</div>
					)}
				</div>
			</div>

			{/* Invisible element used as a target for scrolling */}
			<div ref={bottomRef} className="mt-6"></div>
		</>
	)
}

export default NurseLunchBreakComponent
